import { createElement, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '@/contexts/auth-context';
import { useOrganization } from '@/contexts/organization-context';
import { applyRealtimeAuthToken } from '@/hooks/realtime-auth-prime';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { isAuthCircuitOpen } from '@/lib/auth-circuit';
import { handleAuthCircuitBreak } from '@/lib/auth-circuit-actions';
import {
  getDesktopBridge,
  isDesktopDndActive,
  parseDesktopNotificationInsert,
  resolveDesktopNotificationPath,
  type DesktopAuthCallback,
  type PublicPulseDesktopBridge,
} from '@/lib/desktop/bridge';
import { ensureSupabaseAuthSession } from '@/lib/mobile/auth-persistence';
import { NATIVE_RESUME_EVENT, runAppResume } from '@/lib/mobile/resume-pipeline';

import {
  DESKTOP_OAUTH_CALLBACK_RECEIVED_EVENT,
  NATIVE_OAUTH_FINISHED_EVENT,
} from './use-deep-link-auth';
import { useNotificationPreferences } from './use-notification-preferences';
import { useMarkNotificationAsRead } from './use-notification-mutations';
import { useNotificationsRealtime } from './use-notifications-realtime';
import { useUnreadNotificationCount } from './use-notifications';

import type { RealtimePostgresChangesPayload } from '@supabase/supabase-js';
import type { ReactElement } from 'react';
import type { NavigateFunction } from 'react-router-dom';

interface DesktopAuthMessages {
  readonly failedTitle: string;
  readonly failedDescription: string;
  readonly cancelledTitle: string;
  readonly cancelledDescription: string;
}

type DesktopAuthToast = (input: {
  title: string;
  description: string;
  variant: 'destructive';
}) => void;

function showDesktopAuthError(
  callback: Extract<DesktopAuthCallback, { kind: 'error' }>,
  navigate: NavigateFunction,
  toast: DesktopAuthToast,
  messages: DesktopAuthMessages,
): void {
  const cancelled = callback.reason === 'cancelled';
  toast({
    title: cancelled ? messages.cancelledTitle : messages.failedTitle,
    description: cancelled ? messages.cancelledDescription : messages.failedDescription,
    variant: 'destructive',
  });
  Promise.resolve(navigate('/auth/login', { replace: true })).catch(() => null);
}

async function processDesktopAuthCallback(
  callback: DesktopAuthCallback,
  navigate: NavigateFunction,
  toast: DesktopAuthToast,
  messages: DesktopAuthMessages,
): Promise<void> {
  if (callback.kind === 'error') {
    showDesktopAuthError(callback, navigate, toast, messages);
    return;
  }

  const { data, error } = await supabase.auth.exchangeCodeForSession(callback.code);
  if (error) {
    console.error('[DesktopAuth] Code exchange failed');
    showDesktopAuthError({ kind: 'error', reason: 'failed' }, navigate, toast, messages);
    return;
  }

  const sessionReady = await ensureSupabaseAuthSession(supabase, data.session);
  if (!sessionReady) {
    console.error('[DesktopAuth] Session hydration failed');
    showDesktopAuthError({ kind: 'error', reason: 'failed' }, navigate, toast, messages);
    return;
  }

  Promise.resolve(navigate('/auth/callback', { replace: true })).catch(() => null);
}

interface NotificationsRealtimeMountProps {
  readonly onInsert?: (payload: RealtimePostgresChangesPayload<Record<string, unknown>>) => void;
}

function NotificationsRealtimeMount({ onInsert }: NotificationsRealtimeMountProps = {}): null {
  useNotificationsRealtime(onInsert ? { onInsert } : {});
  return null;
}

interface DesktopBridgeMountProps {
  readonly bridge: PublicPulseDesktopBridge;
}

function DesktopBridgeMount({ bridge }: DesktopBridgeMountProps): ReactElement {
  const { t } = useTranslation('auth');
  const { toast } = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { currentOrganizationId } = useOrganization();
  const { data: preferences, isSuccess: preferencesReady } = useNotificationPreferences();
  const { data: unreadCount } = useUnreadNotificationCount();
  const { mutate: markNotificationAsRead } = useMarkNotificationAsRead();

  useEffect(() => {
    bridge.setBadgeCount(user?.id ? (unreadCount ?? 0) : 0);
  }, [bridge, unreadCount, user?.id]);

  useEffect(() => {
    return bridge.onNavigate(({ path, notificationId }) => {
      markNotificationAsRead(notificationId);
      Promise.resolve(navigate(resolveDesktopNotificationPath(path))).catch(() => null);
    });
  }, [bridge, markNotificationAsRead, navigate]);

  useEffect(() => {
    let processing = false;
    const messages: DesktopAuthMessages = {
      failedTitle: t('oauth.failedTitle'),
      failedDescription: t('oauth.failedDescription'),
      cancelledTitle: t('oauth.cancelledTitle'),
      cancelledDescription: t('oauth.cancelledDescription'),
    };

    return bridge.onAuthCallback((callback) => {
      if (processing) {
        return;
      }
      processing = true;
      globalThis.window.dispatchEvent(new Event(DESKTOP_OAUTH_CALLBACK_RECEIVED_EVENT));
      processDesktopAuthCallback(callback, navigate, toast, messages)
        .catch(() => {
          console.error('[DesktopAuth] Callback handling failed');
          showDesktopAuthError({ kind: 'error', reason: 'failed' }, navigate, toast, messages);
        })
        .finally(() => {
          globalThis.window.dispatchEvent(new Event(NATIVE_OAUTH_FINISHED_EVENT));
          processing = false;
        });
    });
  }, [bridge, navigate, t, toast]);

  useEffect(() => {
    return bridge.onLifecycle((event) => {
      if (event === 'resume') {
        runAppResume({
          supabase,
          queryClient,
          applyRealtimeAuthToken,
          dispatchRealtimeRejoin: () => {
            globalThis.dispatchEvent(new Event(NATIVE_RESUME_EVENT));
          },
          isAuthCircuitOpen,
          tripCircuit: () => handleAuthCircuitBreak(queryClient, 'desktop_resume_refresh_terminal'),
        }).catch((error: unknown) => {
          const name = error instanceof Error ? error.name : 'UnknownError';
          console.error('[Resume] Desktop resume failed', { name });
        });
        return;
      }
      queryClient
        .invalidateQueries({ queryKey: ['notifications'], refetchType: 'active' })
        .catch(() => null);
    });
  }, [bridge, queryClient]);

  return createElement(NotificationsRealtimeMount, {
    onInsert: (payload) => {
      const notification = parseDesktopNotificationInsert(payload, currentOrganizationId, user?.id);
      if (!notification || !preferencesReady || isDesktopDndActive(preferences)) {
        return;
      }
      bridge.showNotification({
        id: notification.id,
        actionPath: notification.actionPath,
        silent: preferences?.sound_enabled === false,
      });
    },
  });
}

export function DesktopIntegrationMount(): ReactElement {
  const bridge = getDesktopBridge();
  return bridge
    ? createElement(DesktopBridgeMount, { bridge })
    : createElement(NotificationsRealtimeMount);
}
